/**
 * Intelligent parser for Harvest estimate line items
 * Handles markdown-style formatting with * for bold and • for bullets
 */

interface ParsedEstimate {
  executiveSummary: string;
  deliverables: Array<{
    id: string;
    title: string;
    description: string;
    dueDate: string;
    status: 'pending';
    order: number;
  }>;
  deliverablesParsed?: Array<{ title: string; description: string }>; // Raw parsed data
  tasks: string[];
  outOfScope: string;
  assumptions: string;
  paymentSchedule?: {
    description: string;
    breakdown: string[];
  };
  licensing?: {
    description: string;
    amount: number;
  };
}

/**
 * Parse a Harvest estimate into structured data for CLM
 */
export function parseHarvestEstimate(estimate: any): ParsedEstimate {
  const result: ParsedEstimate = {
    executiveSummary: '',
    deliverables: [],
    tasks: [],
    outOfScope: '',
    assumptions: '',
  };

  console.log('🔍 Processing', estimate.line_items?.length || 0, 'line items');
  
  // Process each line item
  estimate.line_items?.forEach((item: any, index: number) => {
    const description = item.description || '';
    console.log(`📄 Line item ${index + 1}: ${description.substring(0, 100)}...`);
    
    // Check if this is a licensing line item (usually separate)
    if (item.kind?.toLowerCase().includes('license') || 
        description.toLowerCase().includes('licensing') ||
        description.toLowerCase().includes('license')) {
      console.log('💡 Detected licensing line item');
      result.licensing = {
        description: description.trim(),
        amount: item.amount || 0,
      };
      return;
    }

    // Parse the main description which contains sections
    console.log('🔍 Parsing line item description for sections...');
    const parsed = parseLineItemDescription(description);
    
    if (parsed.executiveSummary) {
      result.executiveSummary = parsed.executiveSummary;
    }
    
    if ((parsed as any).deliverablesParsed && (parsed as any).deliverablesParsed.length > 0) {
      result.deliverables.push(...(parsed as any).deliverablesParsed.map((d: any, i: number) => ({
        id: `harvest-${item.id}-${i}`,
        title: d.title,
        description: d.description,
        dueDate: '', // Will be set to +30 days in converter
        status: 'pending' as const,
        order: result.deliverables.length + i + 1,
      })));
      result.deliverablesParsed = (parsed as any).deliverablesParsed;
    }
    
    if (parsed.tasks.length > 0) {
      result.tasks.push(...parsed.tasks);
    }
  });

  // Parse notes for out of scope, assumptions, and payment schedule
  if (estimate.notes) {
    const notesParsed = parseNotes(estimate.notes);
    result.outOfScope = notesParsed.outOfScope;
    result.assumptions = notesParsed.assumptions;
    result.paymentSchedule = notesParsed.paymentSchedule;
  }

  return result;
}

/**
 * Parse a line item description that contains multiple sections
 */
function parseLineItemDescription(description: string): {
  executiveSummary: string;
  deliverables: string[];
  tasks: string[];
} {
  const result = {
    executiveSummary: '',
    deliverables: [] as string[],
    tasks: [] as string[],
  };

  // Normalize line breaks
  const normalized = description.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  console.log('📝 Normalized description (first 200 chars):', normalized.substring(0, 200));

  // Extract sections using regex
  const execSummaryMatch = normalized.match(/\*Executive Summary\*\n([\s\S]*?)(?=\n\*|$)/i);
  const deliverablesMatch = normalized.match(/\*Deliverables\*\n([\s\S]*?)(?=\n\*|$)/i);
  const tasksMatch = normalized.match(/\*Tasks\*\n([\s\S]*?)(?=\n\*|$)/i);
  
  console.log('🔍 Section matches:', {
    execSummary: !!execSummaryMatch,
    deliverables: !!deliverablesMatch,
    tasks: !!tasksMatch,
  });

  // Extract executive summary
  if (execSummaryMatch) {
    result.executiveSummary = execSummaryMatch[1]
      .trim()
      .replace(/\*([^*]+)\*/g, '$1') // Remove * markdown
      .replace(/\n\n+/g, '\n\n'); // Normalize multiple line breaks
    
    console.log('✅ Executive Summary extracted:', result.executiveSummary.substring(0, 100) + '...');
  } else {
    console.log('⚠️ No Executive Summary section found');
  }

  // Extract deliverables with smart heading/description parsing
  if (deliverablesMatch) {
    const parsed = extractDeliverablesWithHeadings(deliverablesMatch[1]);
    console.log(`📦 Extracted ${parsed.length} deliverables with hierarchical parsing`);
    parsed.forEach((d, i) => {
      console.log(`  ${i + 1}. Title: "${d.title.substring(0, 50)}..."`);
      console.log(`     Description: ${d.description.split('\n').length} lines`);
    });
    result.deliverables = parsed.map(d => d.title); // For now, just store titles
    // Store full parsed deliverables for later use
    (result as any).deliverablesParsed = parsed;
  }

  // Extract tasks
  if (tasksMatch) {
    result.tasks = extractBulletPoints(tasksMatch[1]);
  }

  return result;
}

/**
 * Extract bullet points from text (handles • and - bullets)
 * Simple version: just returns bullet list
 */
function extractBulletPoints(text: string): string[] {
  return text
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.startsWith('•') || line.startsWith('-') || line.startsWith('*'))
    .map(line => line.replace(/^[•\-*]\s*/, '').trim())
    .filter(line => line.length > 0);
}

/**
 * Extract deliverables with headings and nested bullets
 * Handles hierarchical formats:
 * Phase 1: Discovery & Preparation
 * • Prep Sessions: Team Site, Kick Off deck
 * • Pre-Kick Off: Stakeholder alignment
 * Phase 2: Pre-Discovery Workshops
 * • M365 Access & Environment Readiness...
 */
function extractDeliverablesWithHeadings(text: string): Array<{title: string; description: string}> {
  const deliverables: Array<{title: string; description: string}> = [];
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  
  let currentPhase: {title: string; bullets: string[]} | null = null;
  
  for (const line of lines) {
    // Check if this is a phase heading (not a bullet, contains "Phase" or starts with number+colon)
    const isPhaseHeading = !line.startsWith('•') && !line.startsWith('-') && !line.startsWith('*') &&
                          (line.match(/^Phase \d+/i) || line.match(/^\d+[\.:]\s/));
    
    if (isPhaseHeading) {
      // Save previous phase if exists
      if (currentPhase) {
        deliverables.push({
          title: currentPhase.title,
          description: currentPhase.bullets.map(b => `• ${b}`).join('\n'),
        });
      }
      
      // Start new phase
      currentPhase = {
        title: line,
        bullets: [],
      };
    } else if (line.startsWith('•') || line.startsWith('-') || line.startsWith('*')) {
      // This is a bullet point
      const content = line.replace(/^[•\-*]\s*/, '').trim();
      
      if (currentPhase) {
        // Add to current phase
        currentPhase.bullets.push(content);
      } else {
        // No phase yet, treat as standalone deliverable with colon parsing
        const colonIndex = content.indexOf(':');
        if (colonIndex > 0) {
          const title = content.substring(0, colonIndex).trim();
          const description = content.substring(colonIndex + 1).trim();
          deliverables.push({ title, description });
        } else {
          const firstSentence = content.split('.')[0];
          deliverables.push({
            title: firstSentence.length > 60 ? content.substring(0, 60) + '...' : firstSentence,
            description: content,
          });
        }
      }
    }
  }
  
  // Don't forget the last phase
  if (currentPhase) {
    deliverables.push({
      title: currentPhase.title,
      description: currentPhase.bullets.map(b => `• ${b}`).join('\n'),
    });
  }
  
  return deliverables;
}

/**
 * Parse notes section for out of scope, assumptions, and payment schedule
 */
function parseNotes(notes: string): {
  outOfScope: string;
  assumptions: string;
  paymentSchedule?: {
    description: string;
    breakdown: string[];
  };
} {
  const result = {
    outOfScope: '',
    assumptions: '',
    paymentSchedule: undefined as any,
  };

  // Normalize line breaks
  const normalized = notes.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Extract "Out of scope"
  const outOfScopeMatch = normalized.match(/•\s*Out of scope:\s*([^\n•]+)/i);
  if (outOfScopeMatch) {
    result.outOfScope = outOfScopeMatch[1].trim();
  }

  // Extract "Assumptions"
  const assumptionsMatch = normalized.match(/•\s*Assumptions:\s*([^\n]+(?:\n(?!•)[^\n]+)*)/i);
  if (assumptionsMatch) {
    result.assumptions = assumptionsMatch[1].trim();
  }

  // Extract payment schedule
  const paymentScheduleMatch = normalized.match(/Payment Schedule:\n([\s\S]*?)(?:\n\n|$)/i);
  if (paymentScheduleMatch) {
    const scheduleText = paymentScheduleMatch[1];
    const breakdown = scheduleText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.startsWith('-'))
      .map(line => line.replace(/^-\s*/, '').trim());
    
    result.paymentSchedule = {
      description: 'Payment Schedule',
      breakdown,
    };
  }

  return result;
}

/**
 * Convert parsed estimate to Contract form data
 */
export function estimateToContractData(estimate: any, parsed: ParsedEstimate) {
  const executiveSummary = parsed.executiveSummary || estimate.subject || '';
  console.log('📝 Mapping Executive Summary:', executiveSummary ? `${executiveSummary.substring(0, 100)}...` : '(empty)');
  
  // Calculate default expiration date (30 days from today)
  const today = new Date();
  const expirationDate = new Date(today);
  expirationDate.setDate(expirationDate.getDate() + 30);
  const expirationDateString = expirationDate.toISOString().split('T')[0]; // YYYY-MM-DD format
  
  return {
    clientName: estimate.client?.name || estimate.client_name,
    executiveSummary,
    expirationDate: expirationDateString,
    deliverables: parsed.deliverables,
    // Store tasks in notes for now (could be a separate field)
    notes: [
      parsed.tasks.length > 0 && '**Tasks:**\n' + parsed.tasks.map(t => `• ${t}`).join('\n'),
      parsed.outOfScope && `**Out of Scope:**\n${parsed.outOfScope}`,
      parsed.assumptions && `**Assumptions:**\n${parsed.assumptions}`,
    ].filter(Boolean).join('\n\n'),
    // Payment info
    payments: parsed.paymentSchedule?.breakdown.map((p, i) => ({
      id: crypto.randomUUID(),
      description: p,
      amount: extractAmountFromText(p),
      dueDate: i === 0 ? estimate.issue_date : '',
      status: 'pending' as const,
      isRecurring: false,
    })) || [{
      id: crypto.randomUUID(),
      description: 'Total estimate amount',
      amount: estimate.amount || 0,
      dueDate: estimate.issue_date,
      status: 'pending' as const,
      isRecurring: false,
    }],
  };
}

/**
 * Extract dollar amount from text like "50% to start ($8,000)"
 */
function extractAmountFromText(text: string): number {
  const match = text.match(/\$([0-9,]+(?:\.[0-9]{2})?)/);
  if (match) {
    return parseFloat(match[1].replace(/,/g, ''));
  }
  return 0;
}

